import React, { lazy, Suspense, useEffect } from "react";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "./components/Toast";
import { SidebarProvider } from "./context/SidebarContext";
import { ThemeProvider } from "./context/ThemeContext";
import { UserProvider } from "./context/userContext";
import { TokiProvider } from "./context/TokiContext";
import { HambiProvider } from "./context/HambiContext";
import { RegionProvider } from "./context/RegionContext";
import { RoleProvider } from "./context/RoleContext";
import { BrowserRouter as Router, Route, Routes, Navigate, useSearchParams } from "react-router-dom";
import { employerAdPath } from "./utils/employerAdLink";
import Layout from "./Layouts/Layout";
import AuthGuard from "./components/AuthGuard";
import RoleSwitchGuard from "./components/RoleSwitchGuard";
import ProtectedRoute from "./components/ProtectedRoute";
import { HomeSkeleton, MapSkeleton } from "./components/skeletons";
import { loadJobsByLocation } from "./routes/lazyRoutes";
import ErrorBoundary from "./components/ErrorBoundary";
import "./i18n/i18n";
import { initSourceTracking } from "./utils/sourceDetection";
import { initVisitTracking } from "./utils/visitTracking";
import { initErrorTracking } from "./utils/errorTracking";
import { initVersionCheck } from "./utils/versionCheck";
import TrackingRoot from "./components/TrackingRoot";
import { persistOptions, splitPersister } from "./lib/queryPersist";

// Run source detection SYNCHRONOUSLY before any component renders.
// This must happen before useEffect in child components (Home, JobDetail)
// which read sessionStorage to send sourceChannel with view tracking calls.
initSourceTracking();
// Then the visit itself (after the source, which it reports) and error capture — both
// from the first moment, so a visitor who leaves or crashes before React mounts still counts.
initVisitTracking();
initErrorTracking();
// A page left open across a deploy reloads into the new version (see versionCheck.js)
initVersionCheck();

// Lazy load all route components for optimal bundle size
// Skeletons will show during loading - users won't notice
const loadHome = () => import("./page/Home");
// Landing on the home page: start downloading it now, alongside the translations, rather
// than only once the first render asks for it
if (window.location.pathname === "/") loadHome();
const Home = lazy(loadHome);
const ChatLayout = lazy(() => import("./Layouts/ChatLayout"));
const Chat = lazy(() => import("./page/Chat"));
const Companies = lazy(() => import("./page/Companies"));
const Interviews = lazy(() => import("./page/Interviews"));
const Notifications = lazy(() => import("./page/Notifications"));
const DNA = lazy(() => import("./page/DNA"));
const Contact = lazy(() => import("./page/Contact"));
const SavedJobs = lazy(() => import("./page/SavedJobs"));
const Profile = lazy(() => import("./page/Profile"));
const Resume = lazy(() => import("./page/Resume"));
const Feedback = lazy(() => import("./page/Feedback"));
const JobDetail = lazy(() => import("./page/JobDetail"));
const CompanyDetail = lazy(() => import("./page/CompanyDetail"));
const DiilPlus = lazy(() => import("./page/DiilPlus"));
const TokiDemo = lazy(() => import("./components/TokiDemo"));
const Tests = lazy(() => import("./page/Tests"));
const TestTake = lazy(() => import("./page/TestTake"));
const TestResults = lazy(() => import("./page/TestResults"));
const DirectTestAccess = lazy(() => import("./page/DirectTestAccess"));
const EmployerLinkLogin = lazy(() => import("./page/EmployerLinkLogin"));
const BookingCalendar = lazy(() => import("./page/calendar/BookingCalendar"));
const Meetings = lazy(() => import("./page/Meetings"));
const Availability = lazy(() => import("./page/Availability"));
const JobsByRole = lazy(() => import("./page/JobsByRole"));
const Search = lazy(() => import("./page/Search"));
// Shared with the nav controls that preload this chunk on hover/touch
const JobsByLocation = lazy(loadJobsByLocation);
const PreScreenChat = lazy(() => import("./page/PreScreenChat"));
const PostJob = lazy(() => import("./page/employer/PostJob"));
const AdminCvList = lazy(() => import("./page/admin/AdminCvList"));
const AdminJobList = lazy(() => import("./page/admin/AdminJobList"));
const AdminCallShell = lazy(() => import("./page/admin/AdminCallShell"));
const MyCompanies = lazy(() => import("./page/employer/MyCompanies"));
const CandidateSearchDemo = lazy(() => import("./page/CandidateSearchDemo"));
const ParttimeAppInfo = lazy(() => import("./page/ParttimeAppInfo"));
const CandidateProfile = lazy(() => import("./page/CandidateProfile"));

// Create a client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
      // Was 10 minutes. It now has to outlive what the browser keeps on disk: an entry
      // rehydrated from storage and then garbage-collected ten minutes later would be
      // refetched anyway, which is the cost the persisted cache exists to avoid. See
      // lib/queryPersist.js — public data is kept for a day, personal data for the tab.
      gcTime: 24 * 60 * 60 * 1000,
      refetchOnWindowFocus: false, // Don't refetch on tab focus
      retry: 1, // Retry failed requests once
    },
  },
});

// No separate LoadingFallback needed - we use HomeSkeleton directly

/**
 * The old per-ad route, kept only to forward. `?post=` becomes the `?job=` that
 * "Миний зарууд" reads; anything else lands on the list.
 */
const MyPostsRedirect = () => {
  const [params] = useSearchParams();
  const post = params.get("post");
  return <Navigate to={post ? employerAdPath(null, post) : "/employer/companies"} replace />;
};

function App() {
  // Clear all React Query cache on logout/session expiry
  useEffect(() => {
    const handleSessionExpired = () => {
      queryClient.clear();
      // In-memory alone is not enough now that the cache is written to storage: without
      // this, a reload after logout rehydrates the previous user's data.
      splitPersister.removeClient();
    };
    window.addEventListener('auth:sessionExpired', handleSessionExpired);
    return () => window.removeEventListener('auth:sessionExpired', handleSessionExpired);
  }, []);

  return (
    <HelmetProvider>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <RegionProvider>
        <TokiProvider>
          <HambiProvider>
            <UserProvider>
              <RoleProvider>
              <ThemeProvider>
              <SidebarProvider>
                <Router>
                  <TrackingRoot />
                  <ErrorBoundary>
                  <AuthGuard>
                  <Suspense fallback={<HomeSkeleton />}>
                    <Routes>
                      <Route element={<Layout />}>
                        <Route path="/" element={<Home />} />

                    <Route path="chat" element={<ProtectedRoute><ChatLayout /></ProtectedRoute>}>
                      <Route index element={<Chat />} />
                    </Route>

                    <Route path="/companies" element={<Companies />} />

                    <Route path="/user/saved" element={<SavedJobs />} />

                    <Route path="/user/profile" element={<Profile />} />

                    <Route path="/user/resume" element={<Resume />} />

                    <Route path="/user/meetings" element={<Meetings />} />

                    <Route path="/user/applied" element={<SavedJobs />} />

                    <Route path="/user/viewed" element={<SavedJobs />} />

                    {/* Candidate Tests */}
                    <Route path="/tests" element={<Tests />} />
                    <Route path="/tests/pending" element={<Tests />} />
                    <Route path="/tests/completed" element={<Tests />} />
                    <Route path="/tests/all" element={<Tests />} />
                    <Route path="/tests/:id/take" element={<TestTake />} />
                    <Route path="/tests/:id/results" element={<TestResults />} />

                    <Route path="/history" element={<Navigate to="/interviews" replace />} />

                    <Route path="/interviews" element={<Interviews />} />

                    <Route path="/notifications" element={<Notifications />} />

                    <Route path="/availability" element={<Availability />} />

                    <Route path="/jobs" element={<JobsByRole />} />
                    {/* Tapping the mobile header's search bar lands here instead of
                        expanding in place — see partials/Header.jsx's ExpandingSearch. */}
                    <Route path="/search" element={<Search />} />
                    {/* Its own boundary, inside Layout: the app-wide fallback is a job-list
                        skeleton with no header or bottom nav, which on the way to a map reads
                        as a page that failed to load. */}
                    <Route path="/location" element={<Suspense fallback={<MapSkeleton />}><JobsByLocation /></Suspense>} />
                    <Route path="/prescreen" element={<PreScreenChat />} />
                    <Route path="/employer/post" element={<PostJob />} />
                    {/* /employer/my-posts was this ad screen's own route, which meant an
                        ad had two addresses depending on which button opened it. It is
                        "Миний зарууд" now, ad and all; this keeps links already sent out —
                        SMS, notifications, bookmarks — arriving at the same ad. */}
                    <Route path="/employer/my-posts" element={<MyPostsRedirect />} />
                    {/* The admin's ads across every company they posted for — the same
                        CompanyDetail chrome, in aggregate mode. Its own address so
                        /company/:id is never anything but a company page. */}
                    {/* "Миний зарууд" is its own page now, not CompanyDetail in aggregate mode: it is a
                        working list with the CV bank's two-pane shape, while CompanyDetail is a public
                        document about one business. /company/:id still renders that one. */}
                    <Route path="/employer/companies" element={<MyCompanies />} />
                    {/* The admin CV bank. Two paths, one screen: the id is which CV is
                        open, so opening one is a link you can send and closing it is the
                        back button. The page itself refuses non-admins, and so does the
                        server — see DiilAdminCvService. */}
                    {/* One phone line for all four admin routes (AdminCallShell): a call an
                        admin places from a CV must not hang up when the CV is closed. */}
                    <Route element={<AdminCallShell />}>
                    <Route path="/admin/cv" element={<AdminCvList />} />
                    <Route path="/admin/cv/:candidateId" element={<AdminCvList />} />
                    {/* The admin job board, same arrangement: the id is which ad's
                        applicants are open. A sibling of /admin/cv rather than a child —
                        the sidebar marks a row active with startsWith, so nesting it
                        would light up "CV сан" too. */}
                    <Route path="/admin/jobs" element={<AdminJobList />} />
                    <Route path="/admin/jobs/:jobId" element={<AdminJobList />} />
                    </Route>
                    <Route path="/job-group" element={<CandidateSearchDemo />} />
                    <Route path="/parttime-app" element={<ParttimeAppInfo />} />

                    <Route path="/workdna" element={<DNA />} />

                    <Route path="/contact" element={<Contact />} />

                    <Route path="/feedback" element={<Feedback />} />

                    <Route path="/job/:id" element={<JobDetail />} />

                    <Route path="/company/:id" element={<CompanyDetail />} />
                    {/* Same page, reached via a shared link's cosmetic slug suffix
                        (CompanyDetail.jsx's shareCompany) — :slug is never read, only
                        :id, so this renders identically to the plain URL above. */}
                    <Route path="/company/:id/:slug" element={<CompanyDetail />} />

                    {/* A candidate's public profile — reached by tapping a name/photo
                        on a job-group post/comment (see CandidateSearchDemo.jsx). Not
                        the signed-in user's own /user/profile: read-only, no
                        job/application relationship required, and only ever shows the
                        partial CV the backend's public-profile endpoint returns. */}
                    <Route path="/candidate/:id" element={<CandidateProfile />} />

                    <Route path="/games" element={<DiilPlus />} />
                    {/* Calendar Booking - public page with main layout */}
                    <Route path="/booking/:url" element={<BookingCalendar />} />
                  </Route>

                  {/* Direct Test Access - for email/SMS links with optional token auth */}
                  <Route path="/test/:testCandidateId" element={<DirectTestAccess />} />

                  {/* One-click employer sign-in from the "anket ирсэн" SMS. Short path
                      because it travels in a text message; single-use server-side. */}
                  <Route path="/e/:token" element={<EmployerLinkLogin />} />

                  {/* Toki Demo - тестлэх хуудас */}
                  <Route path="/toki-demo" element={<TokiDemo />} />
                </Routes>
                  </Suspense>
                  </AuthGuard>
                  <RoleSwitchGuard />
                  </ErrorBoundary>
              </Router>
              <ToastContainer />
            </SidebarProvider>
              </ThemeProvider>
              </RoleProvider>
          </UserProvider>
        </HambiProvider>
      </TokiProvider>
        </RegionProvider>
    </PersistQueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
